import { useLocation } from "react-router-dom";

import { useDebounce } from "@/hooks/useDebounce";
import useListControls from "@/hooks/useListControls";
import useProductFilters from "@/tpo/shop/hooks/useProductFilters";
import { useQuery } from "@apollo/client";

import useFilters from "./useFilters";

export default function useList({
  connectionName,
  query,
  defaultSort,
  sortOptions: _sortOptions,
  queryVariables,
  showFilters = true
}) {
  const { testingServices, sampleTypes, categories } = useFilters();

  const { toggleFilter, resetFilters, allFilters, validatedFilters } = useProductFilters({
    defaultFilters: {
      categories: [],
      sampleTypes: [],
      testingServices: []
    },
    allFilters: {
      testingServices: [...testingServices].sort(),
      categories: [...categories].sort(),
      sampleTypes: [...sampleTypes].sort()
    }
  });

  const emptyValidatedFilters = {
    categories: [],
    sampleTypes: [],
    testingServices: []
  };

  const location = useLocation();
  const search = new URLSearchParams(location.search).get("search") || "";

  const sortOptions = _sortOptions || [
    {
      label: "Title asc",
      value: "title"
    },
    {
      label: "Title desc",
      value: "-title"
    },
    {
      label: "Start time asc",
      value: "start_time"
    },
    {
      label: "Start time desc",
      value: "-start_time"
    }
  ];

  const controls = useListControls({
    defaultSort,
    sortOptions
  });

  const debouncedSearch = useDebounce(search, 200);

  const activeValidatedFilters = showFilters ? validatedFilters : emptyValidatedFilters;

  const { data, loading, fetchMore } = useQuery(query, {
    variables: {
      first: 10,
      orderBy: controls.sort,
      search: debouncedSearch,
      ...(showFilters
        ? {
            testingServices: activeValidatedFilters.testingServices.join(","),
            healthAreas: activeValidatedFilters.categories.join(","),
            sampleTypes: activeValidatedFilters.sampleTypes.join(",")
          }
        : {}),
      ...queryVariables
    }
  });

  const hasMore = data?.[connectionName].pageInfo.hasNextPage;
  const endCursor = data?.[connectionName].pageInfo.endCursor;
  const objects = data?.[connectionName].edges.map(edge => edge.node);

  return {
    search,
    loading,
    fetchMore,
    hasMore,
    endCursor,
    objects,
    controls,
    toggleFilter,
    resetFilters,
    allFilters,
    validatedFilters: activeValidatedFilters,
    location
  };
}
